/**
 * The decisions behind the "Who is this?" control -- Task 6.7 -- kept out of
 * the components so they can be tested without Flarum's rendering harness
 * (the same split as composerIdentity.ts at 4.1).
 *
 * Nothing here decides who may resolve a handle, and nothing here can grant
 * it: `canResolveHandles` is answered by the server (ForumResourceFields), and
 * the lookup endpoint checks the permission again on every request. What these
 * functions decide is only what the screen offers.
 */

/**
 * A real handle is four lowercase words joined by hyphens (§4.3). Anything
 * else the API sends in the `handle` slot -- the unresolved placeholder,
 * `[unknown participant]` -- names nobody and would only earn a 404.
 */
const HANDLE_SHAPE = /^[a-z]+(?:-[a-z]+){3}$/;

/**
 * Which handle, if any, the control should offer to look up on a post. Null
 * means the control is not drawn at all.
 *
 * **HIDDEN, NOT DISABLED** (§9, 6.7). A control that is shown and then fails
 * teaches everyone who sees it that the mapping exists and is one click away.
 * So it appears only for a viewer the server says may use it.
 *
 * Not on the viewer's own posts either: they know who they are, and a lookup
 * of themselves would still write a permanent row to the log for nothing.
 */
export function lookupTarget(postHandle, viewerHandle, canResolve) {
  if (!canResolve) return null;
  if (!postHandle || !HANDLE_SHAPE.test(postHandle)) return null;
  if (viewerHandle && viewerHandle === postHandle) return null;
  return postHandle;
}
/**
 * What, if anything, is wrong with a reason before it is sent. The server is
 * the authority and counts the same way (trimmed, in characters); this only
 * saves a round trip -- and a logged refusal -- for a reason that cannot pass.
 */
export function reasonProblem(reason, minLength) {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'empty';
  if ([...trimmed].length < minLength) return 'too_short';
  return null;
}

/**
 * The request body `POST /api/handle-lookups` expects (LookupHandleController).
 *
 * `postId` is the post the handle was seen on (Task 7.4). Since Phase 7 the
 * same text can be held once per language, and the server uses the post to
 * find exactly the handle on it. The dialog always sends it; without it, text
 * held in more than one language is refused rather than guessed at.
 */
export function lookupBody(handle, reason, postId) {
  const attributes = {
    handle,
    reason: reason.trim()
  };
  if (postId) attributes.post = postId;
  return {
    data: {
      attributes
    }
  };
}
/**
 * Read the server's answer. Exactly two outcomes: a username, or an account
 * that no longer exists (erased at 1.7a; the handle stands, the link is gone).
 * Anything else is treated as "deleted" rather than guessed at, because the
 * only other way to fill the dialog would be to invent a name.
 */
export function describeAnswer(attributes) {
  const username = attributes?.username;
  if (attributes?.accountDeleted !== true && typeof username === 'string' && username.length > 0) {
    return {
      kind: 'person',
      username
    };
  }
  return {
    kind: 'deleted'
  };
}